import { IDENTIFY_QUEUE_DB } from '@/lib/storeNames';
import { IDENTIFY_QUEUE_STORE, IDENTIFY_QUEUE_VERSION } from './identifyQueueDb';
import { ownedJobStore } from './ownedJobStore';

export interface IdentifyJob {
  itemId: string;
  touchedAt: number;
  // Tells a touch during a run from the job that ran, even within one millisecond.
  touchId: string;
  nextAttemptAt: number;
  backoffStep: number;
  attempts: number;
  transientAttempts: number;
  failed: boolean;
  // Set by a timed-out upload, so later attempts send only thumbnails.
  thumbnails?: boolean;
}

export type PauseReason =
  | 'offline'
  | 'signed_out'
  | 'rate_limited'
  | 'daily_limit'
  | 'service_unavailable';

export type JobOutcome =
  | 'done'
  | 'drop'
  | 'wait'
  | { unavailable: Exclude<PauseReason, 'daily_limit'> }
  | { unavailable: 'daily_limit'; retryAfterMs: number }
  | 'transient'
  | 'upload_timeout'
  | 'bad_output'
  | 'permanent';
export interface RunOpts {
  thumbnails: boolean;
  /** False once another identity is bound: a post now would go out on that identity's session. */
  stillBound: () => boolean;
}
export type RunJob = (itemId: string, opts: RunOpts) => Promise<JobOutcome>;

export interface JobStore {
  get(itemId: string): Promise<IdentifyJob | undefined>;
  put(job: IdentifyJob): Promise<void>;
  delete(itemId: string): Promise<void>;
  list(): Promise<IdentifyJob[]>;
  /** The store as it is now: a drain pass uses it throughout, so a later rebind can't redirect its writes. */
  pass(): JobStore;
  /** The identity the store is scoped to; undefined for a store with no owner scope. */
  owner(): string | undefined;
}

interface BaseJobStore extends JobStore {
  /** Writes `job` unless a job with its itemId exists; resolves whether it wrote. */
  putIfAbsent(job: IdentifyJob): Promise<boolean>;
}

export interface QueueSnapshot {
  pending: string[];
  failed: string[];
  nextDueAt: number | undefined;
  paused: PauseReason | undefined;
}

export interface IdentifyQueue {
  touch(itemId: string): Promise<void>;
  drain(run: RunJob): Promise<void>;
  wake(): Promise<void>;
  nudge(itemId: string): Promise<void>;
  retry(itemId: string): Promise<void>;
  refresh(): Promise<void>;
  snapshot(): QueueSnapshot;
  subscribe(listener: () => void): () => void;
}

type WithLock = (fn: () => Promise<void>) => Promise<void>;

export const WAIT_MS = 15_000;
export const BACKOFF_MS = [30_000, 120_000, 600_000, 3_600_000];
export const MAX_BAD_OUTPUT = 3;
// A job that never succeeds must end: the sixth transient attempt comes about 2 h 13 min
// after the first (30 s + 2 m + 10 m + 60 m + 60 m of backoff).
export const MAX_TRANSIENT = 6;

export function memoryJobStore(): BaseJobStore {
  const jobs = new Map<string, IdentifyJob>();
  const self: BaseJobStore = {
    get: async (itemId) => {
      const job = jobs.get(itemId);
      return job && { ...job };
    },
    put: async (job) => {
      jobs.set(job.itemId, { ...job });
    },
    putIfAbsent: async (job) => {
      if (jobs.has(job.itemId)) return false;
      jobs.set(job.itemId, { ...job });
      return true;
    },
    delete: async (itemId) => {
      jobs.delete(itemId);
    },
    list: async () => [...jobs.values()].map((j) => ({ ...j })),
    pass: () => self,
    owner: () => undefined,
  };
  return self;
}

export function indexedDbJobStore(
  dbName = IDENTIFY_QUEUE_DB,
  storeName = IDENTIFY_QUEUE_STORE,
): BaseJobStore {
  let db: Promise<IDBDatabase> | undefined;
  const open = () => {
    db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(dbName, IDENTIFY_QUEUE_VERSION);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(storeName, { keyPath: 'itemId' });
      };
      req.onsuccess = () => {
        // iOS drops the connection when the app is backgrounded; reopen on the next operation.
        req.result.onclose = () => {
          db = undefined;
        };
        resolve(req.result);
      };
      req.onerror = () => reject(req.error);
    }).catch((err: unknown) => {
      db = undefined;
      throw err;
    });
    return db;
  };
  async function request<T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest<T>) {
    const conn = await open();
    return new Promise<T>((resolve, reject) => {
      const req = op(conn.transaction(storeName, mode).objectStore(storeName));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  const self: BaseJobStore = {
    get: (itemId) => request<IdentifyJob | undefined>('readonly', (s) => s.get(itemId)),
    put: async (job) => {
      await request('readwrite', (s) => s.put(job));
    },
    putIfAbsent: async (job) => {
      try {
        await request('readwrite', (s) => s.add(job));
        return true;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'ConstraintError') return false;
        throw err;
      }
    },
    delete: async (itemId) => {
      await request('readwrite', (s) => s.delete(itemId));
    },
    list: () => request<IdentifyJob[]>('readonly', (s) => s.getAll()),
    pass: () => self,
    owner: () => undefined,
  };
  return self;
}

// Two open tabs share the IndexedDB queue; the lock keeps them from sending the same job.
function webLock(): WithLock {
  return async (fn) => {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      await fn();
      return;
    }
    await navigator.locks.request('rosa-identify-drain', async () => {
      await fn();
    });
  };
}

const dueAt = (job: IdentifyJob) => Math.max(job.touchedAt, job.nextAttemptAt);

const backoffDelay = (step: number) => BACKOFF_MS[Math.min(step, BACKOFF_MS.length - 1)] ?? 0;

function backoff(job: IdentifyJob, t: number): IdentifyJob {
  return {
    ...job,
    nextAttemptAt: t + backoffDelay(job.backoffStep),
    backoffStep: job.backoffStep + 1,
  };
}

function settle(
  job: IdentifyJob,
  outcome: Exclude<JobOutcome, object>,
  t: number,
): IdentifyJob | undefined {
  switch (outcome) {
    case 'done':
    case 'drop':
      return undefined;
    case 'wait':
      return { ...job, nextAttemptAt: t + WAIT_MS };
    case 'transient': {
      const transientAttempts = job.transientAttempts + 1;
      return transientAttempts >= MAX_TRANSIENT
        ? { ...job, transientAttempts, failed: true }
        : { ...backoff(job, t), transientAttempts };
    }
    case 'upload_timeout':
      return settle({ ...job, thumbnails: true }, 'transient', t);
    case 'bad_output': {
      const attempts = job.attempts + 1;
      return attempts >= MAX_BAD_OUTPUT
        ? { ...job, attempts, failed: true }
        : { ...backoff(job, t), attempts };
    }
    case 'permanent':
      return { ...job, failed: true };
  }
}

export function createIdentifyQueue(
  opts: { store?: JobStore; now?: () => number; withLock?: WithLock } = {},
): IdentifyQueue {
  const store = opts.store ?? defaultStore();
  const now = opts.now ?? Date.now;
  const withLock = opts.withLock ?? webLock();
  const listeners = new Set<() => void>();
  let snap: QueueSnapshot = { pending: [], failed: [], nextDueAt: undefined, paused: undefined };
  let draining = false;
  let rerun = false;
  // The runner from the latest drain call: a graph change hands in a new one mid-pass.
  let runner: RunJob | undefined;
  // In memory only: after a reload the first job probes the provider a little early.
  let pausedUntil = 0;
  let pauseReason: PauseReason | undefined;
  // Counts the provider's outage, not the job's: a job's own failures must not lengthen a pause.
  let pauseStep = 0;

  async function refresh(): Promise<void> {
    const jobs = await store.list();
    const live = jobs.filter((j) => !j.failed);
    const due = live.map(dueAt);
    snap = {
      pending: live.map((j) => j.itemId).sort(),
      failed: jobs
        .filter((j) => j.failed)
        .map((j) => j.itemId)
        .sort(),
      nextDueAt: due.length > 0 ? Math.max(Math.min(...due), pausedUntil) : undefined,
      paused: now() < pausedUntil ? pauseReason : undefined,
    };
    for (const l of listeners) l();
  }

  async function drainOnce(): Promise<void> {
    const run = runner;
    if (!run) return;
    // Earliest due first: the job that caused a pause comes due as it ends, and must not
    // re-probe ahead of the jobs it held back.
    const pass = store.pass();
    const owner = pass.owner();
    const jobs = (await pass.list()).sort(
      (a, b) => dueAt(a) - dueAt(b) || a.itemId.localeCompare(b.itemId),
    );
    for (const job of jobs) {
      // A new runner or identity belongs to another graph: leave the rest of the pass to it.
      if (runner !== run || store.owner() !== owner) {
        rerun = true;
        return;
      }
      const t = now();
      if (t < pausedUntil) return;
      if (job.failed || t < job.nextAttemptAt) continue;
      let abandoned = false;
      const stillBound = () => {
        if (store.owner() === owner) return true;
        abandoned = true;
        return false;
      };
      let outcome: JobOutcome;
      try {
        outcome = await run(job.itemId, { thumbnails: job.thumbnails ?? false, stillBound });
      } catch (err) {
        console.warn('identify job failed', job.itemId, err);
        outcome = 'transient';
      }
      // The runner gave up before posting: the job never ran, so it stays as it was for its owner.
      if (abandoned) {
        rerun = true;
        return;
      }
      const settledAt = now();
      if (typeof outcome === 'object') {
        // The server says how long until its budget resets, so probing sooner only repeats the 429.
        pausedUntil =
          settledAt +
          (outcome.unavailable === 'daily_limit'
            ? outcome.retryAfterMs
            : backoffDelay(pauseStep++));
        pauseReason = outcome.unavailable;
      } else if (outcome === 'done') {
        pauseStep = 0;
      }
      // A touch during the run means newer photos: leave the fresh job for the next pass.
      const current = await pass.get(job.itemId);
      if (!current || current.touchId === job.touchId) {
        const next =
          typeof outcome === 'object'
            ? { ...job, nextAttemptAt: pausedUntil }
            : settle(job, outcome, settledAt);
        if (next) await pass.put(next);
        else await pass.delete(job.itemId);
      }
    }
  }

  return {
    async touch(itemId) {
      await store.put({
        itemId,
        touchedAt: now(),
        touchId: crypto.randomUUID(),
        nextAttemptAt: 0,
        backoffStep: 0,
        attempts: 0,
        transientAttempts: 0,
        failed: false,
      });
      // The pass in progress listed its jobs before this one existed.
      if (draining) rerun = true;
      await refresh();
    },
    async drain(run) {
      runner = run;
      if (draining) {
        rerun = true;
        return;
      }
      draining = true;
      try {
        do {
          rerun = false;
          await withLock(drainOnce);
        } while (rerun);
      } finally {
        draining = false;
        await refresh();
      }
    },
    async wake() {
      // Under the drain lock, so a job settled mid-wake isn't overwritten by its stale copy.
      await withLock(async () => {
        // Neither going online nor signing in changes the server's daily budget.
        if (pauseReason !== 'daily_limit') {
          pausedUntil = 0;
          pauseStep = 0;
        }
        for (const job of await store.list()) {
          if (!job.failed && (job.nextAttemptAt > 0 || job.backoffStep > 0)) {
            await store.put({ ...job, nextAttemptAt: 0, backoffStep: 0 });
          }
        }
      });
      await refresh();
    },
    // Makes an existing job due now without starting one: photos change without the user
    // asking to identify the item, as when an upload commits.
    async nudge(itemId) {
      await withLock(async () => {
        const job = await store.get(itemId);
        if (!job) return;
        // A failed job is revived: it failed on a photo set the commit has since changed.
        const revived = { failed: false, attempts: 0, transientAttempts: 0, backoffStep: 0 };
        await store.put({ ...job, ...(job.failed ? revived : {}), nextAttemptAt: 0 });
      });
      if (draining) rerun = true;
      await refresh();
    },
    async retry(itemId) {
      const job = await store.get(itemId);
      if (!job) return;
      pausedUntil = 0;
      pauseStep = 0;
      await store.put({
        ...job,
        failed: false,
        attempts: 0,
        transientAttempts: 0,
        backoffStep: 0,
        nextAttemptAt: 0,
      });
      await refresh();
    },
    refresh,
    snapshot: () => snap,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// jsdom has no IndexedDB; queue in memory there, as photoQueue does.
function defaultStore(): BaseJobStore {
  return typeof globalThis.indexedDB === 'undefined' ? memoryJobStore() : indexedDbJobStore();
}

const sharedJobs = ownedJobStore<IdentifyJob>(defaultStore());
let shared: IdentifyQueue | undefined;

/** Scopes the shared queue to the signed-in identity; every call on it throws until this runs. */
export function bindIdentifyOwner(owner: string, holds: (itemId: string) => boolean): void {
  sharedJobs.bind(owner, holds);
}

export function getIdentifyQueue(): IdentifyQueue {
  shared ??= createIdentifyQueue({ store: sharedJobs });
  return shared;
}
